"use client";

import { useCallback, useEffect, useRef } from "react";

import cn from "classnames";

import {
  isNavigationMenuNestedGroup,
  isNavigationMenuNestedLeaf,
  type NavigationMenuGroupItem,
  type NavigationMenuNestedGroup,
} from "@/components/header/navigationQuery.types";
import { Text } from "@/components/text/text.component";

import { SiteHeaderMegaMenuLink } from "./siteHeaderMegaMenuLink.component";
import { SiteHeaderNavAnimatedUnderline } from "./siteHeaderNavAnimatedLink.component";

const HOVER_CLOSE_DELAY_MS = 150;

export const SiteHeaderMegaMenu = ({ menu }: SiteHeaderMegaMenuProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelScheduledClose();
    detailsRef.current?.removeAttribute("open");
  }, [cancelScheduledClose]);

  const openOnHover = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      if (event.pointerType !== "mouse") return;
      cancelScheduledClose();
      detailsRef.current?.setAttribute("open", "");
    },
    [cancelScheduledClose],
  );

  const scheduleCloseOnHover = useCallback(
    (event: React.PointerEvent<HTMLLIElement>) => {
      if (event.pointerType !== "mouse") return;
      cancelScheduledClose();
      closeTimeoutRef.current = setTimeout(() => {
        detailsRef.current?.removeAttribute("open");
        closeTimeoutRef.current = null;
      }, HOVER_CLOSE_DELAY_MS);
    },
    [cancelScheduledClose],
  );

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const details = detailsRef.current;
      if (!details?.open) return;
      const target = event.target as Node | null;
      if (!target || details.contains(target)) return;
      details.removeAttribute("open");
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      cancelScheduledClose();
    };
  }, [cancelScheduledClose]);

  const children = menu.items ?? [];
  const columns = children.filter(isNavigationMenuNestedGroup);
  const leafItems = children.filter(isNavigationMenuNestedLeaf);
  const isMultiColumn = columns.length > 0;

  const renderLeafItems = (column: NavigationMenuNestedGroup) => {
    return (column.items ?? [])
      .filter((item) => item._type === "navigationItem")
      .map((item) => <SiteHeaderMegaMenuLink key={item._key} item={item} />);
  };

  const renderColumns = () => {
    if (!isMultiColumn) {
      return (
        <ul
          data-testid="site-header-mega-menu-list"
          className="flex min-w-55 flex-col gap-6">
          {leafItems.map((item) => (
            <SiteHeaderMegaMenuLink key={item._key} item={item} />
          ))}
        </ul>
      );
    }

    return (
      <div
        data-testid="site-header-mega-menu-columns"
        className="flex items-start">
        {columns.map((column, index) => (
          <div
            key={column._key}
            data-testid="site-header-mega-menu-column"
            className={cn(
              index > 0 && "border-l border-black/10 pl-10",
              index < columns.length - 1 && "pr-10",
            )}>
            {column.label && (
              <Text variant="body3" className="mb-6">
                {column.label}
              </Text>
            )}
            <ul
              data-testid="site-header-mega-menu-list"
              className="flex flex-col gap-6">
              {renderLeafItems(column)}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <li
      className="list-none"
      data-testid="site-header-main-menu-list-item"
      onPointerEnter={openOnHover}
      onPointerLeave={scheduleCloseOnHover}>
      <details
        ref={detailsRef}
        name="site-header-main-menu"
        data-testid="site-header-mega-menu"
        className="group/details inline-block">
        <summary
          data-testid="site-header-mega-menu-summary"
          className={cn(
            "cursor-pointer list-none [&::-webkit-details-marker]:hidden",
            "group text-promo-black relative inline-block py-4 font-bold tracking-wider whitespace-nowrap uppercase",
          )}>
          <Text variant="preHeaderMd" component="span" className="block">
            {menu.label}
          </Text>
          <SiteHeaderNavAnimatedUnderline showWhenOpen />
        </summary>
        <nav
          data-testid="site-header-mega-menu-nav"
          aria-label={`${menu.label} menu`}
          className="absolute top-full left-1/2 z-50 w-screen max-w-[100vw] -translate-x-1/2 bg-white pt-8 pb-10"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) close();
          }}>
          <div className="container px-6">{renderColumns()}</div>
        </nav>
      </details>
    </li>
  );
};

export type SiteHeaderMegaMenuProps = {
  menu: NavigationMenuGroupItem;
};
